import { LoaderButton } from "@/components/LoaderButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAxios } from "@/context/axios/AxiosContext";
import useGetCashDeskRest from "@/hooks/react-query/Cash/useGetCashDeskRest";
import { querykeys } from "@/hooks/react-query/constants";
import { converToNumberFormat } from "@/utils/convertToNumber";
import { formatDate } from "@/utils/formatDate";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CurrencyInput from "@/components/input/CurrencyInput";
import { useToast } from "@/hooks/use-toast";
import { ErrorMapper } from "@/utils/errorMapping";
import { useCashdeskStore } from "@/context/cashdesk/CashdeskContext";
import { LockOpen, Lock, Calendar, User } from "lucide-react";
import { AvisoDeCajasSinConexion } from "@/components/caja/AvisoDeCajasSinConexion";

interface LocalCardProps {
  local: any;
}

function CashdeskWarehouseCard({ local }: LocalCardProps) {
  const [amountToOpen, setAmountToOpen] = useState("");
  const [isInputError, setIsInputError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onCloseCashdeskModal = useCashdeskStore.use.onClose();

  const CASH_DESK_STATUS_OPEN = "open";

  const { cashDesk, items } = useGetCashDeskRest(
    [
      {
        fieldName: "warehouseId",
        operation: "_eq",
        variableName: "$warehouseId",
        variableValue: local.id,
        type: "String",
        operator: "where",
      },
      {
        fieldName: "status",
        operation: "_eq",
        variableName: "$status",
        variableValue: CASH_DESK_STATUS_OPEN,
        type: "String",
        operator: "where",
      },
    ],
    !!local.id,
    local.id ? [local.id] : [],
    false
  );

  const openMutation = useMutation(
    () =>
      axios.post("/treasury/cashdesks/open", {
        warehouseId: local.id,
        openedAmount: `${converToNumberFormat(
          amountToOpen ? amountToOpen : 0
        )}`,
      }),
    {
      onSuccess: async (response) => {
        if (response.data.isFailure) {
          throw new Error();
        }

        await queryClient.invalidateQueries([querykeys.cashDesk]);

        toast({
          title: "Caja abierta",
          description: "La caja se abrió con éxito.",
          variant: "success",
          duration: 3000,
        });

        setAmountToOpen("");
        onCloseCashdeskModal();
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Hubo un error al abrir la caja. Por favor, intente nuevamente. ${
            error.errorCode ? ErrorMapper.getMessage(error.errorCode) : ""
          }`,
          variant: "error",
          duration: 3000,
        });
      },
    }
  );

  const closeMutation = useMutation(
    () => axios.put(`/treasury/cashdesks/${items[0].id}/close`),
    {
      onSuccess: async (response) => {
        if (response.data.isFailure) {
          throw new Error();
        }

        await queryClient.invalidateQueries([querykeys.cashDesk]);

        toast({
          title: "Caja cerrada",
          description: "La caja se cerró con éxito.",
          variant: "success",
          duration: 3000,
        });

        setDialogOpen(false);
        onCloseCashdeskModal();
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Hubo un error al cerrar la caja. Por favor, intente nuevamente. ${
            error.errorCode ? ErrorMapper.getMessage(error.errorCode) : ""
          }`,
          variant: "error",
          duration: 3000,
        });
      },
    }
  );

  const onOpenCash = () => {
    if (!amountToOpen) {
      setIsInputError(true);
      return;
    }
    openMutation.mutate();
  };

  const isOpen = cashDesk.isSuccess && items.length > 0;

  const cashDeskData = isOpen ? items[0] : null;

  const openedByUser = cashDeskData?.rel_user_open;

  return (
    <div className="w-full border border-border rounded-lg p-3 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm">{local.name}</span>

        {cashDesk.isLoading && <Skeleton className="h-5 w-16 rounded-full" />}

        {cashDesk.isSuccess && (
          <Badge variant={isOpen ? "success" : "secondary"}>
            {isOpen ? "Abierta" : "Cerrada"}
          </Badge>
        )}
      </div>

      {cashDesk.isLoading && <Skeleton className="h-8 w-full rounded-sm" />}

      {cashDesk.isError && (
        <p className="text-sm text-destructive">
          No se pudo traer la información sobre la caja
        </p>
      )}

      {cashDesk.isSuccess && isOpen && cashDeskData && (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar size={12} />
              <span>Abierta el {formatDate(cashDeskData.openedAt)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <User size={12} />
              <span>
                {openedByUser
                  ? `${openedByUser.name} ${openedByUser.lastName}`
                  : "Sin datos del usuario"}
              </span>
            </div>
          </div>

          <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="w-full">
                <Lock size={14} />
                Cerrar caja
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cerrar caja</AlertDialogTitle>
                <AlertDialogDescription>
                  ¿Estás seguro que deseas cerrar la caja de{" "}
                  <span className="font-semibold text-foreground">
                    {local.name}
                  </span>
                  ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AvisoDeCajasSinConexion warehouseId={local.id} />
              <AlertDialogFooter>
                <Button
                  variant="ghost"
                  size="sm"
                  className="border border-border bg-transparent hover:bg-background-200 transition hover:border-text-primary hover:text-text-primary"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <LoaderButton
                  size="sm"
                  className="bg-error-light text-error-foreground shadow-2xs hover:bg-error-light/90 border border-transparent hover:border hover:border-error-foreground transition"
                  onClick={() => closeMutation.mutate()}
                  isLoading={closeMutation.isLoading}
                >
                  <Lock size={14} />
                  Cerrar caja
                </LoaderButton>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {cashDesk.isSuccess && !isOpen && (
        <div className="flex flex-col gap-2">
          <CurrencyInput
            id={`amountToOpen-${local.id}`}
            name="amountToOpen"
            label="Monto de apertura"
            onValueChange={(value) => {
              setIsInputError(false);
              setAmountToOpen(value);
            }}
            decimalsLimit={3}
            value={amountToOpen}
            placeholder="Ingrese el monto..."
            clearInput={() => {
              setAmountToOpen("");
            }}
            helperText="Utilice una coma (,) para separar los decimales"
            isRequired
            isInvalid={isInputError}
            error={["Debe ingresar un monto"]}
            inputClassname="border border-border"
          />
          <LoaderButton
            onClick={onOpenCash}
            isLoading={openMutation.isLoading}
            size="sm"
            variant="success"
            className="w-full"
          >
            <LockOpen size={14} />
            Abrir caja
          </LoaderButton>
        </div>
      )}
    </div>
  );
}

export default CashdeskWarehouseCard;
